
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useWorkspace } from "./useWorkspace";

export interface Plan {
  id: string;
  name: string;
  price_monthly: number | null;
  price_yearly: number | null;
  max_clients: number | null;
  max_members: number | null;
  max_storage_gb: number | null;
  max_posts_month: number | null;
  max_ai_generations_month: number | null;
  features: Record<string, boolean> | null;
}

export interface Subscription {
  id?: string;
  status: string | null;
  current_period_end?: string | null;
  current_period_start?: string | null;
  billing_period?: "monthly" | "yearly" | null;
  billing_type?: "PIX" | "BOLETO" | "CREDIT_CARD" | null;
  asaas_customer_id?: string | null;
  asaas_subscription_id?: string | null;
  asaas_payment_id?: string | null;
  last_payment_error?: string | null;
  plan: Plan;
}

export interface Usage {
  storage_used_bytes: number;
  storage_limit_bytes: number | null;
  storage_limit_gb: number | null;
  clients_used: number;
  clients_limit: number | null;
  members_used: number;
  members_limit: number | null;
  posts_used: number;
  posts_limit: number | null;
  ai_generations_used: number;
  ai_generations_limit: number | null;
}

export function useSubscription() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const { data: subscription, isLoading: loadingSub } = useQuery({
    queryKey: ["subscription", currentWorkspace?.id],
    queryFn: async () => {
      if (!user || !currentWorkspace) return null;

      const [{ data: usageData, error: usageError }, { data: workspaceData, error: workspaceError }, { data: sub, error: subError }] = await Promise.all([
        supabase.rpc("get_workspace_usage", {
          p_workspace_id: currentWorkspace.id,
        }),
        supabase
          .from("workspaces")
          .select(`
            id,
            plan:plans (
              id,
              name,
              price_monthly,
              price_yearly,
              max_clients,
              max_members,
              max_storage_gb,
              max_posts_month,
              max_ai_generations_month,
              features
            )
          `)
          .eq("id", currentWorkspace.id)
          .single(),
        supabase
        .from("subscriptions")
        .select(`
          id,
          status,
          current_period_start,
          current_period_end,
          billing_period,
          billing_type,
          asaas_customer_id,
          asaas_subscription_id,
          asaas_payment_id,
          last_payment_error
        `)
        .eq("workspace_id", currentWorkspace.id)
        .order("updated_at", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      ]);

      if (usageError) throw usageError;
      if (workspaceError) throw workspaceError;
      if (subError) throw subError;

      const usage = (usageData ?? {}) as Record<string, unknown>;
      const plan = workspaceData?.plan as Plan | null;

      if (!plan) {
        return null;
      }

      return {
        id: sub?.id,
        status: sub?.status ?? null,
        current_period_start: sub?.current_period_start ?? null,
        current_period_end: sub?.current_period_end ?? null,
        billing_period: (sub?.billing_period as "monthly" | "yearly" | null | undefined) ?? null,
        billing_type: (sub?.billing_type as "PIX" | "BOLETO" | "CREDIT_CARD" | null | undefined) ?? null,
        asaas_customer_id: sub?.asaas_customer_id ?? null,
        asaas_subscription_id: sub?.asaas_subscription_id ?? null,
        asaas_payment_id: sub?.asaas_payment_id ?? null,
        last_payment_error: sub?.last_payment_error ?? null,
        plan,
        usage,
      };
    },
    enabled: !!user && !!currentWorkspace,
  });

  const usage: Usage = {
    storage_used_bytes: Number(subscription?.usage?.storage_used_bytes ?? 0),
    storage_limit_bytes:
      subscription?.usage?.storage_limit_bytes == null
        ? null
        : Number(subscription.usage.storage_limit_bytes),
    storage_limit_gb:
      subscription?.usage?.storage_limit_gb == null
        ? null
        : Number(subscription.usage.storage_limit_gb),
    clients_used: Number(subscription?.usage?.clients_used ?? 0),
    clients_limit:
      subscription?.usage?.clients_limit == null
        ? null
        : Number(subscription.usage.clients_limit),
    members_used: Number(subscription?.usage?.members_used ?? 0),
    members_limit:
      subscription?.usage?.members_limit == null
        ? null
        : Number(subscription.usage.members_limit),
    posts_used: Number(subscription?.usage?.posts_used ?? 0),
    posts_limit:
      subscription?.usage?.posts_limit == null
        ? null
        : Number(subscription.usage.posts_limit),
    ai_generations_used: Number(subscription?.usage?.ai_generations_used ?? 0),
    ai_generations_limit:
      subscription?.usage?.ai_generations_limit == null
        ? null
        : Number(subscription.usage.ai_generations_limit),
  };

  const checkLimit = (feature: "clients" | "storage" | "ai" | "members" | "posts" | "ai_generations") => {
    if (!subscription) return false;
    if (feature === "clients") return usage.clients_limit === null || usage.clients_used < usage.clients_limit;
    if (feature === "members") return usage.members_limit === null || usage.members_used < usage.members_limit;
    if (feature === "posts") return usage.posts_limit === null || usage.posts_used < usage.posts_limit;
    if (feature === "storage") return usage.storage_limit_bytes === null || usage.storage_used_bytes < usage.storage_limit_bytes;
    if (feature === "ai" || feature === "ai_generations") {
      return usage.ai_generations_limit === null || usage.ai_generations_used < usage.ai_generations_limit;
    }
    return true;
  };

  const checkWorkspaceLimit = async (
    feature: "clients" | "members" | "storage" | "posts" | "ai_generations"
  ) => {
    if (!currentWorkspace?.id) return false;

    const { data, error } = await supabase.rpc("check_workspace_limit", {
      p_workspace_id: currentWorkspace.id,
      p_limit_type: feature,
    });

    if (error) throw error;
    return !!data;
  };

  const refreshUsage = async () => {
    await queryClient.invalidateQueries({ queryKey: ["subscription", currentWorkspace?.id] });
  };

  return {
    subscription,
    plan: subscription?.plan,
    usage,
    checkLimit,
    checkWorkspaceLimit,
    refreshUsage,
    isLoading: loadingSub,
  };
}
